import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { UserContext } from '../../Context/UserContext';
import { FaArrowLeft, FaTags, FaClock, FaBoxOpen, FaCheckCircle, FaChevronDown, FaThLarge } from 'react-icons/fa';
import CurrencySymbol from '../CurrencySymbol';
import './OffersDisplay.css';

const getImageUrl = (baseUrl, path) => {
    if (!path) return "data:image/svg+xml;charset=UTF-8,%3Csvg%20width%3D%22100%22%20height%3D%22100%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Crect%20width%3D%22100%25%22%20height%3D%22100%25%22%20fill%3D%22%23eee%22%2F%3E%3Ctext%20x%3D%2250%25%22%20y%3D%2250%25%22%20font-family%3D%22Arial%22%20font-size%3D%2212%22%20fill%3D%22%23aaa%22%20dominant-baseline%3D%22middle%22%20text-anchor%3D%22middle%22%3ENo%20Image%3C%2Ftext%3E%3C%2Fsvg%3E";
    if (path.startsWith('http')) return path;
    
    // Check if it's a combo image or regular upload
    const cleanPath = path.replace(/^\//, ''); // Remove leading slash
    const base = (baseUrl || '').replace(/\/$/, ''); // Remove trailing slash
    
    // If it's already an upload path, just append it, otherwise it's a combo image filename
    if (cleanPath.startsWith('uploads/')) {
        return `${base}/${cleanPath}`;
    }
    
    return `${base}/api/combo-images/${path.split('/').pop()}`;
};

const formatPrice = (currency, price) => {
    return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '2px' }}>
            <CurrencySymbol currencyCode={currency} size={14} />
            {(Number(price) || 0).toFixed(2)}
        </span>
    );
};

const OfferCard = ({ offer, baseUrl, currency, isFullLayout, animationType, idx, totalCount }) => {
    const images = offer.images && offer.images.length > 0 ? offer.images : [null];
    const displayImages = images.slice(0, 4);
    const gridClass = `multi-image-grid multi-grid-${displayImages.length}`;

    let cardAnim = '';
    let innerAnim1 = '';
    let innerAnim2 = '';

    if (isFullLayout) {
        if (animationType === 'split-horizontal') {
            innerAnim1 = 'anim-from-left';
            innerAnim2 = 'anim-from-right';
        } else if (animationType === 'split-vertical') {
            innerAnim1 = 'anim-from-top';
            innerAnim2 = 'anim-from-bottom';
        } else if (animationType === 'zoom') {
            innerAnim1 = 'anim-zoom-in';
            innerAnim2 = 'anim-zoom-in';
        } else if (animationType === 'flip') {
            innerAnim1 = 'anim-flip-in';
            innerAnim2 = 'anim-flip-in';
        } else if (animationType === 'spin') {
            innerAnim1 = 'anim-spin-in';
            innerAnim2 = 'anim-spin-in';
        }
    } else {
        const half = Math.ceil(totalCount / 2);
        if (animationType === 'split-horizontal') {
            cardAnim = (idx < half) ? 'anim-from-left' : 'anim-from-right';
        } else if (animationType === 'split-vertical') {
            cardAnim = (idx < half) ? 'anim-from-top' : 'anim-from-bottom';
        } else if (animationType === 'zoom') {
            cardAnim = 'anim-zoom-in';
        } else if (animationType === 'flip') {
            cardAnim = 'anim-flip-in';
        } else if (animationType === 'spin') {
            cardAnim = 'anim-spin-in';
        }
    }

    // For single full screen layout, we keep the side-by-side design
    if (isFullLayout) {
        return (
            <div className="offer-card-full">
                <section className={`offer-image-section ${innerAnim1}`}>
                    <div className="main-image-container">
                        <div className={gridClass}>
                            {displayImages.map((img, i) => (
                                <img
                                    key={i}
                                    src={getImageUrl(baseUrl, img)}
                                    alt={offer.description}
                                    className="main-image anim-fade-in"
                                    onError={(e) => { e.target.src = getImageUrl(baseUrl, null); }}
                                />
                            ))}
                        </div>
                        <div className="combo-badge">Exclusive Deal</div>
                    </div>
                </section>
                <section className={`offer-details-section ${innerAnim2}`}>
                    <div className="offer-details-card">
                        <div className="offer-title-row">
                            <h2 className="offer-title">{offer.description || 'Combo Offer'}</h2>
                            {offer.offer_end_time && (
                                <div className="offer-validity">
                                    <FaClock />
                                    Valid until {new Date(offer.offer_end_time).toLocaleDateString()}
                                </div>
                            )}
                        </div>
                        <div className="offer-price-row">
                            <span className="offer-current-price">
                                {formatPrice(currency, offer.offer_price || offer.total_price)}
                            </span>
                            {(offer.offer_price && offer.total_price > offer.offer_price) && (
                                <>
                                    <span className="offer-original-price" style={{ position: 'relative', display: 'inline-block', opacity: 0.7 }}>
                                        {formatPrice(currency, offer.total_price)}
                                        <div style={{ position: 'absolute', top: '50%', left: '-5%', width: '110%', height: '2px', backgroundColor: '#000000', transform: 'translateY(-50%)', zIndex: 1 }}></div>
                                    </span>
                                    <span className="offer-discount-badge">
                                        Save {formatPrice(currency, offer.total_price - offer.offer_price)}!
                                    </span>
                                </>
                            )}
                        </div>
                        {offer.enable_reward_points && offer.reward_points > 0 && (
                            <div style={{ marginTop: '10px', background: 'linear-gradient(135deg, #dcfce7, #d1fae5)', borderRadius: '8px', padding: '8px 16px', display: 'inline-flex', alignItems: 'center', gap: '10px', border: '1px solid #86efac', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                                <span style={{ fontSize: '18px' }}>🎁</span>
                                <span style={{ fontSize: '15px', fontWeight: '800', color: '#15803d' }}>
                                    Earn {offer.reward_points} pts / offer
                                </span>
                            </div>
                        )}
                        {offer.items && offer.items.length > 0 && (
                            <div className="offer-ingredients-box">
                                <h3>What's Included:</h3>
                                <ul className="offer-ingredients-list">
                                    {offer.items.map((item, idx) => {
                                        const imgPath = item.data?.image || item.data?.addon_image || item.data?.combo_image;
                                        const finalUrl = imgPath ? (imgPath.startsWith('http') ? imgPath : `${(baseUrl || '').replace(/\/$/, '')}/${imgPath.replace(/^\//, '')}`) : null;
                                        return (
                                            <li key={idx} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                {finalUrl ? (
                                                    <img src={finalUrl} alt="" style={{ width: '32px', height: '32px', borderRadius: '4px', objectFit: 'cover' }} onError={(e) => { e.target.style.display = "none"; }} />
                                                ) : (
                                                    <FaCheckCircle className="check-icon" />
                                                )}
                                                <span>{item.data?.item_name || item.data?.name1 || 'Item'}</span>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        )}
                    </div>
                </section>
            </div>
        );
    }

    // For grid layouts (1x2, 2x1, 2x2), we use a stacked card design
    return (
        <div className={`offer-card-compact ${cardAnim}`}>
            <div className="compact-image-container">
                <div className={gridClass}>
                    {displayImages.map((img, i) => (
                        <img
                            key={i}
                            src={getImageUrl(baseUrl, img)}
                            alt={offer.description}
                            className="compact-image anim-fade-in"
                            onError={(e) => { e.target.src = getImageUrl(baseUrl, null); }}
                        />
                    ))}
                </div>
                <div className="combo-badge-compact">Deal</div>
            </div>
            <div className="compact-details">
                <h3 className="compact-title">{offer.description || 'Combo Offer'}</h3>
                <div className="compact-price-row">
                    <span className="compact-current-price">
                        {formatPrice(currency, offer.offer_price || offer.total_price)}
                    </span>
                    {(offer.offer_price && offer.total_price > offer.offer_price) && (
                        <span className="compact-original-price" style={{ position: 'relative', display: 'inline-block', opacity: 0.7 }}>
                            {formatPrice(currency, offer.total_price)}
                            <div style={{ position: 'absolute', top: '50%', left: '-5%', width: '110%', height: '2px', backgroundColor: '#000000', transform: 'translateY(-50%)', zIndex: 1 }}></div>
                        </span>
                    )}
                </div>
                {offer.enable_reward_points && offer.reward_points > 0 && (
                    <div style={{ marginBottom: '8px', background: '#dcfce7', borderRadius: '6px', padding: '4px 8px', display: 'inline-flex', alignItems: 'center', gap: '6px', border: '1px solid #86efac' }}>
                        <span style={{ fontSize: '12px' }}>🎁</span>
                        <span style={{ fontSize: '12px', fontWeight: '700', color: '#15803d' }}>{offer.reward_points} pts</span>
                    </div>
                )}
                {offer.items && offer.items.length > 0 && (
                    <div className="compact-ingredients">
                        <ul className="compact-ingredients-list">
                            {offer.items.slice(0, 3).map((item, idx) => {
                                const imgPath = item.data?.image || item.data?.addon_image || item.data?.combo_image;
                                const finalUrl = imgPath ? (imgPath.startsWith('http') ? imgPath : `${(baseUrl || '').replace(/\/$/, '')}/${imgPath.replace(/^\//, '')}`) : null;
                                return (
                                    <div key={idx} className="compact-item" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        {finalUrl ? (
                                            <img src={finalUrl} alt="" style={{ width: '20px', height: '20px', borderRadius: '4px', objectFit: 'cover' }} onError={(e) => { e.target.style.display = "none"; }} />
                                        ) : (
                                            <FaCheckCircle className="compact-check" />
                                        )}
                                        <span>{item.data?.item_name || item.data?.name1 || 'Item'}</span>
                                    </div>
                                );
                            })}
                            {offer.items.length > 3 && <li><span className="more-items">+ {offer.items.length - 3} more</span></li>}
                        </ul>
                    </div>
                )}
            </div>
        </div>
    );
};


const OffersDisplay = ({ permissions }) => {
    const { baseUrl, getHeaders, configLoading } = useContext(UserContext);
    const navigate = useNavigate();
    const [offers, setOffers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currency, setCurrency] = useState('INR');

    const [currentIndex, setCurrentIndex] = useState(0);
    const [progress, setProgress] = useState(0);
    const [isPaused, setIsPaused] = useState(false);
    const [slideKey, setSlideKey] = useState(0);

    // Configurations
    const [animationType, setAnimationType] = useState('split-horizontal');
    const [slideDurationSec, setSlideDurationSec] = useState(10);
    const [rows, setRows] = useState(1);
    const [columns, setColumns] = useState(1);
    const [theme, setTheme] = useState('theme-light');
    const [cardStyle, setCardStyle] = useState('style-card');
    const [animSpeed, setAnimSpeed] = useState('0.8s');
    const [showHeader, setShowHeader] = useState(true);

    useEffect(() => {
        const handleKeyDown = (e) => {
            const isInput = e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.isContentEditable;
            if (!isInput && !e.ctrlKey && !e.altKey && !e.metaKey && e.key.toLowerCase() === 'h') {
                e.preventDefault();
                setShowHeader(prev => !prev);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    useEffect(() => {
        if (configLoading) return;

        const fetchData = async () => {
            try {
                const curRes = await axios.get(`${baseUrl || ''}/api/settings`, { headers: getHeaders() });
                if (curRes.data && curRes.data.currency) {
                    setCurrency(curRes.data.currency.toUpperCase());
                }

                const res = await axios.get(`${baseUrl || ''}/api/combo-offer`, { headers: getHeaders() });
                const allOffers = res.data || [];

                const now = new Date();
                const validOffers = allOffers.filter(c => {
                    if (c.offer_end_time && new Date(c.offer_end_time) < now) return false;
                    return true;
                });
                setOffers(validOffers);
            } catch (err) {
                console.error("Error fetching offers", err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [baseUrl, configLoading, getHeaders]);

    const offersPerPage = rows * columns;
    const isFullLayout = (rows === 1 && columns === 1);

    // Timer Logic
    useEffect(() => {
        if (isPaused || offers.length === 0) {
            return;
        }

        const intervalTime = 50; // Update progress every 50ms
        const step = (intervalTime / (slideDurationSec * 1000)) * 100;

        const timer = setInterval(() => {
            setProgress((prev) => {
                if (prev >= 100) {
                    setCurrentIndex((prevIndex) => (prevIndex + offersPerPage) % offers.length);
                    setSlideKey(k => k + 1);
                    return 0;
                }
                return prev + step;
            });
        }, intervalTime);

        return () => clearInterval(timer);
    }, [offers.length, isPaused, currentIndex, slideDurationSec, offersPerPage]);

    // Handle Layout Change (reset index if needed)
    useEffect(() => {
        setProgress(0);
        setCurrentIndex(0);
        setSlideKey(k => k + 1);
    }, [rows, columns]);


    if (loading) {
        return (
            <div className="offer-loading-container">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    if (offers.length === 0) {
        return (
            <div className="offer-empty-container">
                <div className="offer-empty-card">
                    <FaBoxOpen size={64} color="#dee2e6" />
                    <h2>No Active Offers</h2>
                    <p>Check back later for exciting new deals!</p>
                    <button onClick={() => navigate('/home')} className="back-btn-empty">Go Back</button>
                </div>
            </div>
        );
    }

    // Get the active group of offers to render
    const visibleOffers = offers.slice(currentIndex, currentIndex + offersPerPage);

    return (
        <div className={`offer-slideshow-wrapper ${theme !== 'theme-light' ? theme : ''} ${cardStyle}`} style={{ '--anim-duration': animSpeed }}>
            {/* Header Area */}
            {showHeader && (
            <header className="offer-header">
                <div className="header-left">
                    <button onClick={() => navigate('/home')} className="back-btn">
                        <FaArrowLeft /> Back
                    </button>
                    <h1><FaTags color="#ef233c" /> Special Offers</h1>
                </div>

                <div className="header-right" style={{ display: 'flex', gap: '15px', alignItems: 'center', flexWrap: 'wrap' }}>

                    {/* Style Dropdown */}
                    <div className="offer-dropdown-container">
                        <select
                            value={cardStyle}
                            onChange={(e) => setCardStyle(e.target.value)}
                            className="offer-dropdown"
                            style={{ minWidth: '130px' }}
                        >
                            <option value="style-card">Card Style</option>
                            <option value="style-no-card">No Card</option>
                        </select>
                        <FaChevronDown className="dropdown-icon" />
                    </div>

                    {/* Theme Dropdown */}
                    <div className="offer-dropdown-container">
                        <select
                            value={theme}
                            onChange={(e) => setTheme(e.target.value)}
                            className="offer-dropdown"
                            style={{ minWidth: '130px' }}
                        >
                            <option value="theme-light">Light Classic</option>
                            <option value="theme-sunrise">Warm Sunrise</option>
                            <option value="theme-mint">Mint Fresh</option>
                            <option value="theme-lavender">Soft Lavender</option>
                        </select>
                        <FaChevronDown className="dropdown-icon" />
                    </div>

                    {/* Layout Inputs */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#F3F6FB', padding: '8px 16px', borderRadius: '12px', border: '1.5px solid transparent' }}>
                        <span style={{ fontWeight: '700', color: '#64748b', fontSize: '14px' }}>Rows:</span>
                        <input
                            type="number"
                            min="1"
                            max="10"
                            value={rows}
                            onChange={(e) => setRows(Math.max(1, Number(e.target.value)) || 1)}
                            style={{ width: '40px', border: 'none', background: 'transparent', outline: 'none', fontWeight: '800', color: '#1e293b', fontSize: '15px', textAlign: 'center' }}
                        />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#F3F6FB', padding: '8px 16px', borderRadius: '12px', border: '1.5px solid transparent' }}>
                        <span style={{ fontWeight: '700', color: '#64748b', fontSize: '14px' }}>Cols:</span>
                        <input
                            type="number"
                            min="1"
                            max="10"
                            value={columns}
                            onChange={(e) => setColumns(Math.max(1, Number(e.target.value)) || 1)}
                            style={{ width: '40px', border: 'none', background: 'transparent', outline: 'none', fontWeight: '800', color: '#1e293b', fontSize: '15px', textAlign: 'center' }}
                        />
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#F3F6FB', padding: '8px 16px', borderRadius: '12px', border: '1.5px solid transparent' }}>
                        <span style={{ fontWeight: '700', color: '#64748b', fontSize: '14px' }}>Time (sec):</span>
                        <input
                            type="number"
                            min="1"
                            max="60"
                            value={slideDurationSec}
                            onChange={(e) => setSlideDurationSec(Math.max(1, Number(e.target.value)) || 10)}
                            style={{ width: '50px', border: 'none', background: 'transparent', outline: 'none', fontWeight: '800', color: '#1e293b', fontSize: '15px', textAlign: 'center' }}
                        />
                    </div>

                    {/* Anim Speed */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#F3F6FB', padding: '8px 16px', borderRadius: '12px', border: '1.5px solid transparent' }}>
                        <span style={{ fontWeight: '700', color: '#64748b', fontSize: '14px' }}>Anim Speed:</span>
                        <select
                            value={animSpeed}
                            onChange={(e) => {
                                setAnimSpeed(e.target.value);
                                setSlideKey(k => k + 1);
                            }}
                            style={{ border: 'none', background: 'transparent', outline: 'none', fontWeight: '800', color: '#1e293b', fontSize: '15px' }}
                        >
                            <option value="0.5s">Fast (0.5s)</option>
                            <option value="0.8s">Normal (0.8s)</option>
                            <option value="1.5s">Slow (1.5s)</option>
                            <option value="3s">Very Slow (3s)</option>
                        </select>
                    </div>

                    {/* Animation Dropdown */}
                    <div className="offer-dropdown-container">
                        <select
                            value={animationType}
                            onChange={(e) => {
                                setAnimationType(e.target.value);
                                setSlideKey(k => k + 1);
                            }}
                            className="offer-dropdown"
                            style={{ minWidth: '160px' }}
                        >
                            <option value="split-horizontal">Left & Right</option>
                            <option value="split-vertical">Top & Bottom</option>
                            <option value="zoom">Zoom Pop</option>
                            <option value="flip">3D Flip</option>
                            <option value="spin">Spin In</option>
                        </select>
                        <FaChevronDown className="dropdown-icon" />
                    </div>
                </div>
            </header>
            )}

            {/* Main Content Area */}
            <main
                key={slideKey}
                className="offer-main-content"
                style={{ gridTemplateColumns: `repeat(${columns}, 1fr)`, gridTemplateRows: `repeat(${rows}, 1fr)` }}
                onMouseEnter={() => setIsPaused(true)}
                onMouseLeave={() => setIsPaused(false)}
            >
                {visibleOffers.map((offer, idx) => (
                    <OfferCard
                        key={offer._id + idx}
                        offer={offer}
                        baseUrl={baseUrl}
                        currency={currency}
                        isFullLayout={isFullLayout}
                        animationType={animationType}
                        idx={idx}
                        totalCount={visibleOffers.length}
                    />
                ))}
            </main>

            {/* Footer Timer/Progress */}
            <div className="offer-footer-timer">
                <div className="offer-timer-text">
                    <span>Showing {Math.min(currentIndex + visibleOffers.length, offers.length)} of {offers.length}</span>
                    <span>{Math.max(0, Math.ceil(((slideDurationSec * 1000) - (progress / 100 * (slideDurationSec * 1000))) / 1000))}s</span>
                </div>
                <div className="offer-progress-bar">
                    <div className="offer-progress-fill" style={{ width: `${progress}%` }}></div>
                </div>
            </div>
        </div>
    );
};

export default OffersDisplay;